import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { api, ApiError } from '../lib/api';
import type { ClientWithSummary } from '@shared/models';

export default function Dashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientWithSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await api.get<ClientWithSummary[]>('/api/clients');
      setClients(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startDiagnostic = async (clientId: number) => {
    try {
      const d = await api.post<{ id: number }>(`/api/clients/${clientId}/diagnostics`, {});
      navigate(`/diagnostics/${d.id}`);
    } catch (e) {
      alert(e instanceof Error ? e.message : '診断の開始に失敗しました');
    }
  };

  const deleteClient = async (id: number, name: string) => {
    if (!confirm(`「${name}」を削除しますか?(関連する診断データもすべて削除されます)`)) return;
    try {
      await api.del(`/api/clients/${id}`);
      await refresh();
    } catch (e) {
      if (e instanceof ApiError) alert(e.message);
    }
  };

  const stats = useMemo(() => {
    const total = clients.length;
    const diagnosed = clients.filter((c) => c.latest_diagnostic_id !== null).length;
    return { total, diagnosed };
  }, [clients]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.industry, c.location].some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [clients, query]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-800">クライアント一覧</h1>
          <Link to="/clients/new" className="btn-primary">
            + 新規クライアント追加
          </Link>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-rank-1">{error}</div>
        )}

        {/* サマリー */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:max-w-md sm:gap-4">
          <StatCard label="登録クライアント" value={stats.total} hint="社" />
          <StatCard label="診断実施済み" value={stats.diagnosed} hint="社" />
        </div>

        {/* 検索 */}
        <div className="mb-3 flex items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
              ⌕
            </span>
            <input
              type="text"
              className="input pl-8"
              placeholder="会社名・業種・所在地で検索"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {!loading && (
            <span className="text-xs text-slate-400">
              {query ? `${filtered.length} / ${clients.length} 社` : `${clients.length} 社`}
            </span>
          )}
        </div>

        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">会社名</th>
                <th className="px-4 py-3">業種</th>
                <th className="px-4 py-3">最新診断日</th>
                <th className="px-4 py-3">入力進捗</th>
                <th className="px-4 py-3">総合スコア</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                    読み込み中...
                  </td>
                </tr>
              )}
              {!loading && clients.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-16">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <p className="text-sm text-slate-500">まだクライアントが登録されていません。</p>
                      <Link to="/clients/new" className="btn-primary">
                        + 最初のクライアントを追加
                      </Link>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && clients.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">
                    「{query}」に一致するクライアントが見つかりません。
                  </td>
                </tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{c.name}</div>
                    {c.location && <div className="text-xs text-slate-400">{c.location}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.industry || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{c.latest_diagnostic_date || '-'}</td>
                  <td className="px-4 py-3">
                    {c.latest_diagnostic_id ? (
                      <ProgressBar filled={c.latest_filled_count} total={c.latest_total_count} />
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ScoreBadge score={c.latest_total_score} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-0.5">
                      {c.latest_diagnostic_id && (
                        <Link
                          to={`/diagnostics/${c.latest_diagnostic_id}`}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-brand-accent hover:bg-brand-accent/10"
                        >
                          診断を開く
                        </Link>
                      )}
                      <button
                        onClick={() => startDiagnostic(c.id)}
                        className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        新規診断
                      </button>
                      <Link
                        to={`/clients/${c.id}/edit`}
                        className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        編集
                      </Link>
                      <button
                        onClick={() => deleteClient(c.id, c.name)}
                        className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-400 hover:bg-rank-1/10 hover:text-rank-1"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 font-num text-2xl font-bold text-slate-800">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function ProgressBar({ filled, total }: { filled: number; total: number }) {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const done = pct >= 100;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${done ? 'bg-rank-5' : 'bg-brand-accent'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-num text-xs text-slate-500">
        {filled}/{total}
      </span>
    </div>
  );
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-sm text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white ${verdictBg(
          score,
        )}`}
      >
        {verdictLabel(score)}
      </span>
      <span className="font-num text-sm font-semibold text-slate-800">{score.toFixed(1)}</span>
    </span>
  );
}

function verdictLabel(score: number): string {
  if (score >= 4.0) return 'A';
  if (score >= 3.0) return 'B';
  if (score >= 2.0) return 'C';
  return 'D';
}

function verdictBg(score: number): string {
  if (score >= 4.0) return 'bg-rank-5';
  if (score >= 3.0) return 'bg-rank-4';
  if (score >= 2.0) return 'bg-rank-2';
  return 'bg-rank-1';
}
